import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';

// components
import { PdpRespDetail } from './PdpRespDetail';
import { PdpSharedDetail } from '../PdpSharedDetails/PdpSharedDetail';
import { PdpSharedDetailCondition } from '../PdpSharedDetails/PdpSharedDetailCondition';
import { PdpSharedDetailFramingOptions } from '../PdpSharedDetails/PdpSharedDetailFramingOptions';
import { PdpSharedDetailReference } from '../PdpSharedDetails/PdpSharedDetailReference';
import { PdpSharedDetailLabReport } from '../PdpSharedDetails/PdpSharedDetailLabReport';

import { detailsTitle } from '../messages/pdpDetailsMessages';

// helpers
import slugify from 'dibs-slugify/exports/slugify';
import get from 'lodash.get';

// styles
import styles from './PdpRespDetails.scss';

const detailsToHideInPrint = ['PRODUCTION_TIME'];

function PdpRespDetailsComponent(props) {
    const { isClient, item, currency, relay, selectedSkuId, user, viewer } = props;
    const details = item.pdpDetailsDisplay;

    const renderDetail = detail => {
        const { detailKey, detailValues, delimiter } = detail;
        if (detailKey === 'CONDITION') {
            return (
                <PdpSharedDetailCondition
                    detailValues={detailValues}
                    currency={currency}
                    delimiter={delimiter}
                    relay={relay}
                    itemId={get(item, 'serviceId')}
                />
            );
        }
        if (detailKey === 'FRAMING') {
            return (
                <PdpSharedDetailFramingOptions
                    detailValues={detailValues}
                    currency={currency}
                    delimiter={delimiter}
                    relay={relay}
                    itemId={get(item, 'serviceId')}
                    item={item}
                    user={user}
                    viewer={viewer}
                />
            );
        }
        if (detailKey === 'REFERENCE_NUMBER') {
            return (
                <PdpSharedDetailReference
                    detailValues={detailValues}
                    currency={currency}
                    delimiter={delimiter}
                    itemId={get(item, 'serviceId')}
                    skuId={selectedSkuId}
                    item={item}
                    viewer={viewer}
                    user={user}
                />
            );
        }

        // Only displayed for mobile view
        // Responsive view shows ring size in the sidebar
        if (detailKey === 'RING_SIZE') {
            return null;
        }

        if (detailKey === 'LAB_REPORT') {
            return (
                <PdpSharedDetailLabReport
                    isClient={isClient}
                    detailValues={detailValues}
                    currency={currency}
                    delimiter={delimiter}
                    selectedSkuId={selectedSkuId}
                />
            );
        }

        return (
            <PdpSharedDetail
                detailValues={detailValues}
                currency={currency}
                delimiter={delimiter}
                selectedSkuId={selectedSkuId}
            />
        );
    };

    return (
        <div className={styles.wrapper} data-tn="pdp-details">
            <h2 className={styles.header}>{detailsTitle}</h2>
            <ul className={styles.detailsWrapper}>
                {details.map((detail, index) => {
                    const { detailKey, detailDisplayName, delimiter } = detail;
                    const shouldHideInPrint = detailsToHideInPrint.includes(detailKey);
                    return (
                        <PdpRespDetail
                            delimiter={delimiter}
                            title={detailDisplayName}
                            dataTn={slugify(detailDisplayName)}
                            key={`${detailKey}-${index}`}
                            printHide={shouldHideInPrint}
                        >
                            {renderDetail(detail)}
                        </PdpRespDetail>
                    );
                })}
            </ul>
        </div>
    );
}

PdpRespDetailsComponent.propTypes = {
    currency: PropTypes.string,
    isClient: PropTypes.bool,
    item: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    selectedSkuId: PropTypes.string,
    user: PropTypes.object,
    viewer: PropTypes.object.isRequired,
};

export const PdpRespDetails = createFragmentContainer(PdpRespDetailsComponent, {
    viewer: graphql`
        fragment PdpRespDetails_viewer on Viewer {
            ...PdpSharedDetailReference_viewer
            ...PdpSharedDetailFramingOptions_viewer
        }
    `,
    item: graphql`
        fragment PdpRespDetails_item on Item {
            serviceId
            pdpDetailsDisplay {
                detailKey
                delimiter
                detailDisplayName
                detailValues {
                    ...PdpSharedDetail_detailValues
                }
            }
            ...PdpSharedDetailFramingOptions_item
            ...PdpSharedDetailReference_item
        }
    `,
    user: graphql`
        fragment PdpRespDetails_user on User {
            ...PdpSharedDetailFramingOptions_user
            ...PdpSharedDetailReference_user
        }
    `,
});
